// What the chrome does that is not a click on a control (PLAN-CLIENT-SPLIT
// §2.3): `game.play(intent)`. Every intent is plain data with a `kind`, and
// `intents.*` builds them — a client never writes the literal.
//
// **Undo and history are log operations, never commands** (§29), which is
// why they are here and not options: the engine cuts or views the log for
// the side this client sits in. The rest are commands the turn row and the
// strips used to send themselves; through here they are bound to the turn
// and to the seat like any click, and refused out loud through the pill.

import {
  BISHOP,
  type BoardEdge,
  type PieceType,
  KING,
  KNIGHT,
  makePiece,
  PAWN,
  QUEEN,
  ROOK
} from "src/shared/board";
import {
  ALTAR,
  FUEL,
  KEYSTONE,
  METAL,
  RELIC,
  SPECIAL,
  STONE,
  STORE,
  TURRET,
  WELL
} from "src/shared/industry";

import type { LevelName, Tuning } from "./drivers";
import type { ResourceName, PieceName, Side, Square, StructureName } from "./types";

/** Which door of the front page a game is opened from. */
export type GameMode =
  /** Two people at one keyboard. */
  | "local"
  /** A room: `room` names one to join, absent creates one. */
  | "net"
  /** One scenario against the machine. */
  | "solo"
  /** The roguelike, from its opening: Normal Mode (§31 G3). */
  | "run"
  /** The short practice run on an authored map (§31 G1). */
  | "training"
  /** The saved run. */
  | "continue"
  /** Two machines on a scenario. */
  | "watch"
  /** A machine playing the run. */
  | "watch-run";

/** What the front door chose. Every id is a `Catalog` id. */
export interface NewGame {
  mode: GameMode;
  /** `catalog.maps()`, for the modes that open a board. Absent is the classic sixty-four. */
  map?: string;
  /** `catalog.scenarios()`, for `solo` and `watch`. */
  scenario?: string;
  level?: LevelName;
  seed?: number;
  room?: string;
  /** Alliance id per side, as a `catalog.alliances()` layout gives it. */
  teams?: readonly number[];
  /** `catalog.armies()` id per side; absent leaves the scenario's own. */
  armies?: readonly (string | undefined)[];
  /** `catalog.profiles()` id per side. */
  profiles?: readonly (string | undefined)[];
  tuning?: Tuning;
  /** What to call you in a room; the server clamps it and may hand back another. */
  name?: string;
}

/**
 * What `game.open` answers once the board is in place. `room` is the room a
 * `net` game sits in — the one named, or the one just made — for the share link.
 */
export type Opened =
  | { ok: true; mode: GameMode; room?: string }
  | { ok: false; refusal: string };

/** The saved run, as a title menu's Continue button describes it. */
export interface SavedRun {
  /** The level's name. */
  level: string;
  plies: number;
  difficulty?: LevelName;
  /** When it was written, epoch ms. */
  saved: number;
  /** Time played, ms, idle gaps capped. */
  played?: number;
}

export type Intent =
  | { kind: "undo" }
  /** Show the board after this many steps; null is "keep up with the game". */
  | { kind: "history"; step: number | null }
  /** End the whole turn, every remaining move forfeited. */
  | { kind: "pass" }
  /**
   * Open a game: `game.open` without waiting for it. A refusal arrives through
   * the session's pill, since a level loads after `play` has answered.
   */
  | { kind: "new-game"; options: NewGame };

// --- the development tools ------------------------------------------------------
//
// What `game.test.dispatch` takes: the engine's own commands, which the
// contract does not name — so these build them, in the contract's words, and
// pack the wire's numbers themselves. **DEV-only by construction**: `game.test`
// is undefined in production, and every one of these is in `SOLO_COMMANDS`,
// which the server refuses outright. A board expansion, a conjured army, a
// handful of iron: things a person debugging a run wants and no player gets.

/** The contract's name for each number on the wire, from the shared vocabulary. */
// No `monster`: retired and never emitted (`PieceName` keeps it for the human tree).
const PIECE_KIND = {
  pawn: PAWN,
  knight: KNIGHT,
  bishop: BISHOP,
  rook: ROOK,
  queen: QUEEN,
  king: KING
} as Record<PieceName, PieceType>;
const STRUCTURE_KIND: Record<StructureName, number> = {
  store: STORE,
  turret: TURRET,
  altar: ALTAR,
  relic: RELIC,
  // Retired (§31 R1): kind 4 is no structure, so the reducer refuses it.
  banner: 4,
  keystone: KEYSTONE,
  well: WELL,
  // Retired (§31 R1): kind 7 is no structure either.
  research: 7
};
const RESOURCE_KIND: Record<ResourceName, number> = {
  metal: METAL,
  stone: STONE,
  special: SPECIAL,
  fuel: FUEL
};

/** A piece to stand up, by name and army. */
export interface Standing {
  square: Square;
  piece: PieceName;
  /** Whose it is: 0 is White, 1 is Black — an army, not a facing. */
  side: Side;
}

/** A structure to stand up, by name and owning army. */
export interface StandingStructure {
  square: Square;
  structure: StructureName;
  side: Side;
}

export interface Grow {
  /** Name an edge and the engine adds the row just beyond it. */
  edge?: BoardEdge;
  /** Or the squares themselves; with `edge`, the two are combined. Cells only ever add. */
  cells?: readonly Square[];
  pieces?: readonly Standing[];
  /** Buildings on bare ground; a square with a piece or a building on it is skipped. */
  structures?: readonly StandingStructure[];
  heaps?: readonly { square: Square; amount: number }[];
  /** Health each piece it stands up has, and armor it wears. Absent is the rank's own, and none. */
  tough?: number;
  plated?: number;
}

/** The structures a run hands out as tokens: the artifacts' own. */
export type ArtifactStructure = Exclude<StructureName, "store" | "turret">;

/** A command as `game.test.dispatch` takes it. */
export type DevCommand = { type: string; [field: string]: unknown };

const defined = <T extends object>(fields: T): T =>
  Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as T;

export const dev = {
  /** Open ground, and optionally stand something on it. */
  grow: (grow: Grow): DevCommand =>
    defined({
      type: "dev-grow",
      edge: grow.edge,
      cells: grow.cells ? [...grow.cells] : undefined,
      pieces: grow.pieces?.map((one) => [one.square, makePiece(one.side, PIECE_KIND[one.piece])]),
      structures: grow.structures?.map((one) => [one.square, STRUCTURE_KIND[one.structure], one.side]),
      heaps: grow.heaps?.map((one) => [one.square, one.amount]),
      tough: grow.tough,
      plated: grow.plated
    }),
  /** Take ground away: an edge's row, or the squares named. */
  collapse: (what: { edge?: BoardEdge; cells?: readonly Square[] }): DevCommand =>
    defined({ type: "dev-collapse", edge: what.edge, cells: what.cells ? [...what.cells] : undefined }),
  /**
   * Material into, or out of, whatever stands on a square — a piece or a
   * building. `resource` is the ladder (metal unless said), `grade` the rung
   * on it; `delta` is signed, and a rung no ladder has is refused.
   */
  material: (
    square: Square,
    material: { resource?: ResourceName; grade: number; delta: number }
  ): DevCommand =>
    defined({
      type: "dev-material",
      square,
      grade: material.grade,
      delta: material.delta,
      // The rules' word for a resource is a material *line*; absent is metal.
      line: material.resource === undefined ? undefined : RESOURCE_KIND[material.resource]
    }),
  /** Beat an army outright, as a mate would. */
  defeat: (side: Side): DevCommand => ({ type: "dev-defeat", side }),
  /** A card into the run's deck, by `catalog.cards()` id. */
  grantCard: (card: string): DevCommand => ({ type: "grant-card", card }),
  /** A structure token into the hand — an artifact's structure; a store is bought, never held. */
  grantToken: (structure: ArtifactStructure): DevCommand => ({
    type: "grant-token",
    kind: STRUCTURE_KIND[structure]
  }),
  /** Open a region the level describes, as the director would; `defeated` sweeps that army with it. */
  revealRegion: (region: string, defeated?: Side): DevCommand =>
    defined({ type: "reveal-region", region, defeated }),
  /** Stamp or clear an army's despair; `immune` exempts it for good. */
  despair: (side: Side, depressed: boolean, immune?: boolean): DevCommand =>
    defined({ type: "set-despair", side, depressed, immune })
} satisfies Record<string, (...args: never[]) => DevCommand>;

// --- the chrome's own acts --------------------------------------------------------

/** The constructors, one per `Intent` arm. */
export const intents = {
  /** The development tools, for `game.test.dispatch`. */
  dev,
  undo: () => ({ kind: "undo" }) as const,
  history: (step: number | null) => ({ kind: "history", step }) as const,
  pass: () => ({ kind: "pass" }) as const,
  newGame: (options: NewGame) => ({ kind: "new-game", options }) as const
};
